import { UserPreferenceContext } from '@contexts/user-preference-context';
import { ApplicationVersion } from '@models/application-version.model';
import VersionService from '@services/version.service';
import { DragAndDropFileUploader, useTranslation } from '@testsigma/atoms';
import { PageLoader } from '@utils/common';
import { useQuery } from 'react-query';
import { useContext, useMemo, useState } from 'react';
import { useReactQueryCaching } from '@hooks/use-react-query-caching';
import { CachingPrefix } from '@enums/caching-prefix.enum';
interface FileSelectorProps {
  onFileChange: (file: File) => void;
  applicationVersionId?: number;
  isStrictMode?: boolean;
}
const androidType: string[] = ['apk', 'image/*', 'zip', 'aab', 'pdf'];
const ipaTypes: string[] = ['ipa', 'app', 'image/*', 'zip', 'pdf'];
const strictAndroidType: string[] = ['apk'];
const strictIpaTypes: string[] = ['ipa', 'app'];
const attachmentTypes: string[] = [
  'apk',
  'ipa',
  'xlsx',
  'xls',
  'image/*',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'txt',
  'pdf',
  'mp4',
  '3gp',
  'zip',
  'log',
  'pps',
  'ppsx',
  'csv',
  'heic',
  'heif',
  'key',
  'm4v',
  'mov',
  'mp3',
  'mpg',
  'odt',
  'ogg',
  'vtt',
  'xml',
  'json',
  'yml',
  'qsf',
  'txl',
  'ts',
];

export const FileSelectorPanel: React.FC<FileSelectorProps> = ({
  onFileChange,
  applicationVersionId,
  isStrictMode,
}) => {
  const { t } = useTranslation();

  const { getCachingTime } = useReactQueryCaching(
    CachingPrefix.UPLOADS_FETCH_VERSION,
    false,
  );
  const { versionId } = useContext(UserPreferenceContext)?.userPreference
    ?.preference?.projectPreference;

  const [typeErrorMessage, setTypeErrorMessage] = useState<string>();
  const [sizeErrorMessage, setSizeErrorMessage] = useState<string>();

  const currentVersionId = useMemo(
    () => (applicationVersionId ? applicationVersionId : versionId),
    [applicationVersionId, versionId],
  );

  const { data: applicationVersion, isLoading } = useQuery(
    [`${CachingPrefix.UPLOADS_FETCH_VERSION}-${currentVersionId}`],
    () => fetchVersion(versionId),
    {
      staleTime: getCachingTime(),
    },
  );

  const fetchVersion = async (versionId: number) => {
    const version: ApplicationVersion = (await VersionService.show(
      versionId,
    )) as ApplicationVersion;
    return version;
  };

  const supportedFileType = useMemo(() => {
    if (applicationVersion?.application?.isAndroidNative) {
      return [...(isStrictMode ? strictAndroidType : androidType)];
    } else if (applicationVersion?.application?.isIosNative) {
      return [...(isStrictMode ? strictIpaTypes : ipaTypes)];
    } else {
      return [...attachmentTypes];
    }
  }, [applicationVersion]);

  return (
    <div className='flex items-center w-full'>
      {isLoading ? (
        <PageLoader top='10%' />
      ) : (
        <DragAndDropFileUploader
          className='w-72'
          onTypeError={setTypeErrorMessage}
          maxSize={1024}
          onSizeError={setSizeErrorMessage}
          handleChange={onFileChange}
          accept={supportedFileType}>
          <div className='flex flex-col items-center gap gap-y-3'>
            <div className='text-small leading-5 text-unique-800'>
              {t('common.file_size.message', { size: '1024 MB' })}
            </div>

            {typeErrorMessage && (
              <div className='text-small leading-5 text-error-700'>{`${t(
                'common.error.message.label',
              )} ${typeErrorMessage}`}</div>
            )}

            {sizeErrorMessage && (
              <div className='text-small leading-5 text-error-700'>{`${t(
                'common.error.message.label',
              )} ${t('common.max_size.error', { size: '1024 MB' })}`}</div>
            )}
          </div>
        </DragAndDropFileUploader>
      )}
    </div>
  );
};
